import { Location, Market, Site, Unit } from '@/common/models';

export const TYPE_COUNTRY = 'country';
export const TYPE_REGION = 'region';
export const TYPE_CITY = 'city';
export const TYPE_SITE = 'site';
export const TYPE_UNIT = 'unit';
export const TYPE_MARKET = 'market';

export class SelectableNode {
  constructor(type, entity, isSelected = false, isExcluded = false, isExpanded = false, parent = null) {
    this.type = type;
    this.entity = entity;
    this.isSelected = isSelected;
    this.isExcluded = isExcluded;
    this.isExpanded = isExpanded;
    this.parent = parent;
    this.childrenLoaded = false;

    switch (type) {
      case TYPE_COUNTRY:
        if (!(entity instanceof Location) || entity.level !== 0) {
          throw new Error('entity is not a country');
        }
        this.children = {
          [TYPE_REGION]: { expanded: false, children: [] },
          [TYPE_MARKET]: { expanded: false, children: [] },
        };
        if (entity.locations && entity.locations.length > 0) {
          this.addChildren(TYPE_REGION, entity.locations, true);
        }
        break;
      case TYPE_REGION:
        if (!(entity instanceof Location) || entity.level !== 1) {
          throw new Error('entity is not a region');
        }
        this.children = {
          [TYPE_CITY]: { expanded: false, children: [] },
        };
        if (entity.locations && entity.locations.length > 0) {
          this.addChildren(TYPE_CITY, entity.locations, true);
        }
        break;
      case TYPE_CITY:
        if (!(entity instanceof Location) || entity.level !== 3) {
          throw new Error('entity is not a region');
        }
        this.children = {
          [TYPE_SITE]: { expanded: false, children: [] },
        };
        if (entity.sites && entity.sites.length > 0) {
          this.addChildren(TYPE_SITE, entity.sites, true);
        }
        break;
      case TYPE_SITE:
        if (!(entity instanceof Site)) {
          throw new Error('entity is not a site');
        }
        this.children = {
          [TYPE_UNIT]: { expanded: false, children: [] },
        };
        if (entity.units && entity.units.length > 0) {
          this.addChildren(TYPE_UNIT, entity.units, true);
        }
        break;
      case TYPE_UNIT:
        if (!(entity instanceof Unit)) {
          throw new Error('entity is not a unit');
        }
        this.children = {};
        break;
      case TYPE_MARKET:
        if (!(entity instanceof Market)) {
          throw new Error('entity is not a market');
        }
        this.children = {
          [TYPE_SITE]: { expanded: false, children: [] },
        };
        if (entity.sites && entity.sites.length > 0) {
          this.addChildren(TYPE_SITE, entity.sites, true);
        }
        break;
      default:
        throw new Error('unknown entity type');
    }
  }

  get id() {
    return this.entity.id;
  }

  get name() {
    return this.entity.name;
  }

  get displayName() {
    return this.entity.displayName;
  }

  get fullPath() {
    if (this.parent === null) {
      return this.displayName;
    }
    return `${this.parent.fullPath} > ${this.displayName}`;
  }

  get isCountry() {
    return this.type === TYPE_COUNTRY;
  }

  get isRegion() {
    return this.type === TYPE_REGION;
  }

  get isCity() {
    return this.type === TYPE_CITY;
  }

  get isSite() {
    return this.type === TYPE_SITE;
  }

  get isScreen() {
    return this.type === TYPE_UNIT;
  }

  get isMarket() {
    return this.type === TYPE_MARKET;
  }

  addChildren(type, children, isExpanded = false) {
    if (!this.children[type]) {
      throw new Error('unknown type');
    }
    this.children[type] = {
      expanded: isExpanded,
      children: children
        .map((c) => {
          if ((type === TYPE_SITE || type === TYPE_UNIT) && !c.isActive) {
            return null;
          }
          return new SelectableNode(type, c, false, false, isExpanded, this);
        })
        .filter((x) => x !== null)
        .sort((a, b) => {
          const nameA = a.displayName.toUpperCase();
          const nameB = b.displayName.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }

          // names must be equal
          return 0;
        }),
    };
    this.childrenLoaded = true;
  }

  select() {
    this.isSelected = true;
  }

  unselect() {
    this.isSelected = false;
  }

  exclude() {
    this.isExcluded = true;
  }

  unexclude() {
    this.isExcluded = false;
  }

  selectChildren(type) {
    if ((this.children[type] || []).length > 0) {
      (this.children[type] || []).forEach((l) => {
        l.select();
      });
    }
  }

  excludeChildren(type) {
    if ((this.children[type] || []).length > 0) {
      (this.children[type] || []).forEach((l) => {
        l.exclude();
      });
    }
  }

  clearSelection() {
    this.isSelected = false;
    Object.keys(this.children).forEach((type) => this.children[type].children.forEach((l) => l.clearSelection()));
  }

  clearExclusion() {
    this.isExcluded = false;
    Object.keys(this.children).forEach((type) => this.children[type].children.forEach((l) => l.clearExclusion()));
  }

  expand() {
    this.isExpanded = true;
  }

  unexpand() {
    this.isExpanded = false;
  }

  changeSelectionState(state) {
    this.isSelected = state;
  }

  changeExclusionState(state) {
    this.isExcluded = state;
  }

  getAllNodes() {
    return [this]
      .concat(Object.values(this.children).map((x) => x.children.map((y) => y.getAllNodes())))
      .flat(Infinity);
  }
}
